import React, { ReactNode } from 'react';

import { useIntl } from 'react-intl';
import { View } from 'react-native';
import { StyleSheet } from 'react-native';

import { IBackendCart, IServerOrder } from '@rbi-ctg/menu';
import { CartItemOfferDiscount } from 'components/cart-item-experiment/cart-item-offer-discount';
import { CartItemOfferDiscountSkeleton } from 'components/cart-item-experiment/cart-item-offer-discount.skeleton';
import Currency from 'components/currency';
import { ServiceMode } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { SkeletonStyled, TotalContainer } from 'pages/cart/payment/styled';
import { GrandTotalLabel } from 'pages/cart/your-cart/totals/styled';
import { useTotalTipCalculations } from 'pages/cart/your-cart/totals/use-total-tip-calculations';
import { selectors, useAppSelector } from 'state/global-state';
import { OrderStatus } from 'state/order/types';
import { theme } from 'styles/configure-theme';

interface StickyFooterProps {
  serverOrder: IServerOrder | null;
  serviceMode: ServiceMode;
  priceTooLowCmp: ReactNode;
  CTAButton: ReactNode;
  shouldSkipPriceOrder: boolean;
  orderStatus: OrderStatus | null;
}

export const CheckOutStickyFooter: React.FC<StickyFooterProps> = ({
  serverOrder,
  serviceMode,
  priceTooLowCmp,
  CTAButton,
  shouldSkipPriceOrder,
  orderStatus,
}) => {
  const { formatMessage } = useIntl();
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const { calculateCartTotalWithDiscount } = useCart();
  const { cart, loading: loadingTipCalculations } = useTotalTipCalculations({
    serverOrder,
    serviceMode,
  });
  const evaluatingIncentives = useAppSelector(selectors.loyalty.selectOffersLoading);
  const appliedLoyaltyDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );

  const { cartTotal = 0, isCartTotalNegative = false } = calculateCartTotalWithDiscount();
  const skipOrderPricing = isSignUpAfterCart || isCartTotalNegative || shouldSkipPriceOrder;
  const isPriceRequested = !serverOrder || orderStatus === OrderStatus.PRICE_REQUESTED;
  const isOrderPriceLoading = !isCartTotalNegative && isPriceRequested && !shouldSkipPriceOrder;
  const offerDiscountIsLoading =
    (loadingTipCalculations && !skipOrderPricing) || isOrderPriceLoading || evaluatingIncentives;

  // If "SignUp After Cart" is enabled:
  // - Use cart preview values
  // Otherwise
  // - Use server cart
  const cartSelected = skipOrderPricing
    ? ({
        totalCents: cartTotal,
        subTotalCents: cartTotal,
      } as IBackendCart)
    : cart;

  return (
    <View testID="cart-payment" style={styles.buttonContainer}>
      {appliedLoyaltyDiscountOffer ? (
        offerDiscountIsLoading ? (
          <CartItemOfferDiscountSkeleton selectedLoyaltyOffer={appliedLoyaltyDiscountOffer} />
        ) : (
          <CartItemOfferDiscount selectedLoyaltyOffer={appliedLoyaltyDiscountOffer} />
        )
      ) : null}
      {priceTooLowCmp}
      {loadingTipCalculations && !skipOrderPricing ? (
        <SkeletonStyled />
      ) : (
        <TotalContainer>
          <GrandTotalLabel fontSize={'xl'} flexGrow={0}>
            {formatMessage({
              id: 'total',
            })}
            {': '}
          </GrandTotalLabel>
          <Currency
            testID="grand-total"
            amount={
              cartSelected?.totalCents && cartSelected?.totalCents > 0 ? cartSelected.totalCents : 0
            }
            isBold
            textProps={{
              color: theme.token('text-default'),
              margin: 0,
              flexGrow: 0,
              fontSize: 'xl',
            }}
          />
        </TotalContainer>
      )}
      {CTAButton}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    alignItems: 'center',
    width: '100%',
  },
});
